import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  GraduationCap, Loader2, CheckCircle2, Upload, AlertCircle, 
  FileText, Calendar as CalendarIcon, Clock
} from 'lucide-react';
import { format } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Extracted DocumentItem component to avoid babel plugin issues
const DocumentItem = ({ doc, onUpload, onPromise, uploading, showCalendar, setShowCalendar, selectedDate, setSelectedDate, submittingPromise, fileInputRef, replaceInputRef }) => {
  const isUploaded = doc.status === 'uploaded';
  
  const handleUploadClick = () => {
    if (fileInputRef) fileInputRef.click();
  };
  
  const handleReplaceClick = () => {
    if (replaceInputRef) replaceInputRef.click();
  };
  
  return (
    <div className={`border rounded-lg p-4 transition-all ${
      isUploaded 
        ? 'bg-green-50 border-green-200' 
        : doc.promised_date 
          ? 'bg-amber-50 border-amber-200'
          : 'bg-white border-slate-200'
    }`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
            isUploaded 
              ? 'bg-green-500 text-white'
              : doc.promised_date
                ? 'bg-amber-500 text-white'
                : 'bg-slate-200 text-slate-500'
          }`}>
            {isUploaded ? (
              <CheckCircle2 className="w-5 h-5" />
            ) : doc.promised_date ? (
              <Clock className="w-5 h-5" />
            ) : (
              <FileText className="w-5 h-5" />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground">{doc.name}</h3>
            {isUploaded && doc.uploaded_file && (
              <p className="text-sm text-green-600 flex items-center gap-1 mt-1">
                <CheckCircle2 className="w-4 h-4" />
                Uploaded: {doc.uploaded_file.filename}
              </p>
            )}
            {doc.promised_date && !isUploaded && (
              <p className="text-sm text-amber-600 flex items-center gap-1 mt-1">
                <CalendarIcon className="w-4 h-4" />
                Reminder set for: {doc.promised_date}
              </p>
            )}
          </div>
        </div>

        {!isUploaded && (
          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={handleUploadClick}
              disabled={uploading}
              size="sm"
              data-testid={`upload-btn-${doc.name}`}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <span className="flex items-center">
                  <Upload className="w-4 h-4 mr-1" />
                  Upload
                </span>
              )}
            </Button>

            <Popover open={showCalendar} onOpenChange={setShowCalendar}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" data-testid={`later-btn-${doc.name}`}>
                  <CalendarIcon className="w-4 h-4 mr-1" />
                  Later
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <div className="p-3 border-b">
                  <p className="text-sm font-medium">When will you upload this?</p>
                  <p className="text-xs text-muted-foreground">We'll send you a reminder</p>
                </div>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date()}
                  initialFocus
                />
                <div className="p-3 border-t flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => setShowCalendar(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1"
                    disabled={!selectedDate || submittingPromise}
                    onClick={onPromise}
                    data-testid={`confirm-date-${doc.name}`}
                  >
                    {submittingPromise ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Set Reminder'
                    )}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        )}

        {isUploaded && (
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={handleReplaceClick}
              disabled={uploading}
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                'Replace'
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const StudentDocumentUploadPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState({});
  const [showCalendar, setShowCalendar] = useState({});
  const [selectedDate, setSelectedDate] = useState({});
  const [submittingPromise, setSubmittingPromise] = useState({});
  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/public/student-documents/${token}`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid or expired link');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (docName, isReplace = false) => {
    const refKey = isReplace ? `replace-${docName}` : docName;
    const file = fileInputRefs.current[refKey]?.files?.[0];
    if (!file) return;

    setUploading(prev => ({ ...prev, [docName]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_name', docName);

      await axios.post(
        `${API}/public/student-documents/${token}/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      await fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(prev => ({ ...prev, [docName]: false }));
      if (fileInputRefs.current[refKey]) {
        fileInputRefs.current[refKey].value = '';
      }
    }
  };

  const handlePromiseSubmit = async (docName) => {
    const date = selectedDate[docName];
    if (!date) {
      alert('Please select a date');
      return;
    }

    setSubmittingPromise(prev => ({ ...prev, [docName]: true }));

    try {
      await axios.post(`${API}/public/student-documents/${token}/promise`, {
        document_name: docName,
        promised_date: format(date, 'yyyy-MM-dd')
      });

      await fetchData();
      setShowCalendar(prev => ({ ...prev, [docName]: false }));
      setSelectedDate(prev => ({ ...prev, [docName]: null }));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to set reminder');
    } finally {
      setSubmittingPromise(prev => ({ ...prev, [docName]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-bold mb-2">Link Invalid</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const allUploaded = data && data.total_pending === 0;
  const documents = data?.documents || [];
  const progressPercent = data && data.total_required > 0 ? (data.total_uploaded / data.total_required) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="font-bold text-lg">{data?.company_name || 'Education Consultant'}</span>
          </div>
          {data?.university_name && (
            <p className="text-sm text-muted-foreground mb-2">Application for {data.university_name}</p>
          )}
          <h1 className="text-2xl font-bold text-foreground">Document Upload Portal</h1>
          <p className="text-muted-foreground mt-1">Welcome, {data?.student_name || 'Student'}</p>
        </div>

        {/* Progress */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Upload Progress</span>
              <span className="text-sm text-muted-foreground">
                {data?.total_uploaded || 0} of {data?.total_required || 0} documents
              </span>
            </div>
            <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-500 to-emerald-500 transition-all duration-500"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            {allUploaded && (
              <div className="flex items-center gap-2 mt-3 text-green-600">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">All documents uploaded!</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Document List */}
        <Card>
          <CardHeader>
            <CardTitle>Required Documents</CardTitle>
            <CardDescription>
              Please upload all required documents for your application
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {documents.length > 0 ? (
              documents.map((doc) => {
                const docName = doc.name;
                return (
                  <div key={docName}>
                    <input
                      type="file"
                      ref={el => { if (el) fileInputRefs.current[docName] = el; }}
                      onChange={() => handleFileUpload(docName)}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      className="hidden"
                      data-testid={`file-input-${docName}`}
                    />
                    <input
                      type="file"
                      ref={el => { if (el) fileInputRefs.current[`replace-${docName}`] = el; }}
                      onChange={() => handleFileUpload(docName, true)}
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      className="hidden"
                    />
                    <DocumentItem
                      doc={doc}
                      onUpload={() => handleFileUpload(docName)}
                      onPromise={() => handlePromiseSubmit(docName)}
                      uploading={uploading[docName]}
                      showCalendar={showCalendar[docName]}
                      setShowCalendar={(val) => setShowCalendar(prev => ({ ...prev, [docName]: val }))}
                      selectedDate={selectedDate[docName]}
                      setSelectedDate={(val) => setSelectedDate(prev => ({ ...prev, [docName]: val }))}
                      submittingPromise={submittingPromise[docName]}
                      fileInputRef={fileInputRefs.current[docName]}
                      replaceInputRef={fileInputRefs.current[`replace-${docName}`]}
                    />
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No documents required at this time.</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Help Text */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
        </p>
        <p className="text-center text-xs text-muted-foreground mt-1">
          Powered by EduBridge Network
        </p>
      </div>
    </div>
  );
};

export default StudentDocumentUploadPage;
